import { AnyAction, Dispatch } from 'redux';
import { updateEntities } from 'redux-query';
import { IUser } from '../../interfaces/user/user';
import { getItem, removeItem, setItem } from '../storage';
import { clearFeatureFlags } from './featureFlags';
import { fetchFeatureFlagsFromAPI } from './featureFlagsInitializer';

export type RepresentVariant = 'clientUser' | 'lqaUser' | 'myLqaClientUser';

export type RepresentNavigation = 'newTab' | 'sameWindow';

export interface CompleteRepresentSessionParams {
  newUser: IUser;
  newToken: string;
  dispatch: Dispatch<AnyAction>;
  variant?: RepresentVariant;
  navigation: RepresentNavigation;
  /** Optional override for tests; defaults to window.open / location.href. */
  onNavigate?: (navigation: RepresentNavigation) => void;
}

const navigateAfterRepresent = (navigation: RepresentNavigation) => {
  if (navigation === 'newTab') {
    window.open('/', '_blank');
  } else {
    window.location.href = '/';
  }
};

const setOriginalUser = (user: IUser, token: string) => {
  removeItem('originalUser');
  setItem(
    'originalUser',
    JSON.stringify({
      user,
      token,
    })
  );
};

/**
 * Session update, feature flag refresh, and navigation shared by user list
 * represent handlers and RepresentApp on success. Mirrors production handlers (BUG-850).
 */
export async function completeRepresentSession(params: CompleteRepresentSessionParams): Promise<void> {
  const { newUser, newToken, dispatch, variant, navigation, onNavigate } = params;

  const originalUser = getItem('originalUser');
  if (originalUser) {
    removeItem('originalUser');
    setItem('user', JSON.stringify(originalUser));
  }

  const { user, token } = getItem('user');
  setOriginalUser(user, token);

  dispatch(
    updateEntities({
      activeProfile: () => newUser,
      originalUser: () => user,
    })
  );

  if (variant === 'lqaUser') {
    removeItem('lqaUser');
    setItem(
      'lqaUser',
      JSON.stringify({
        user: newUser,
        token: newToken,
      })
    );
  }

  removeItem('user');
  setItem(
    'user',
    JSON.stringify({
      user: newUser,
      token: newToken,
    })
  );

  try {
    await refreshFeatureFlagsForSession();
  } catch (error) {
    // tslint:disable-next-line:no-console
    console.error('Failed to initialize feature flags after user representation:', error);
  }

  // RepresentApp uses sameWindow (consult-app demo). List handlers use newTab.
  if (onNavigate) {
    onNavigate(navigation);
  } else {
    navigateAfterRepresent(navigation);
  }
}

/** Target behavior for BUG-850 fix: clear stale flags and refetch for the new session. */
export async function refreshFeatureFlagsForSession(): Promise<void> {
  clearFeatureFlags();
  await fetchFeatureFlagsFromAPI();
}
